import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Building2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Package,
  Star,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Settings,
  QrCode,
  ArrowUpDown,
  Sun,
  Moon,
  Sparkles,
  Layers,
  Calendar,
  Clock,
  BarChart3
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { config } from "../lib/config";

interface ProductStats {
  product_id: string;
  product_name: string;
  sku: string;
  brand: string | null;
  form: string;
  review_count: number;
  avg_stars: number;
  repeat_purchase_count: number;
  no_repeat_purchase_count: number;
  recent_low_ratings: number;
  last_review_date: string | null;
  // Detailed rating averages
  avg_scent_rating: number | null;
  avg_dryness_rating: number | null;
  avg_taste_rating: number | null;
  avg_appearance_rating: number | null;
  avg_packaging_rating: number | null;
  best_used_day: number;
  best_used_night: number;
  best_used_both: number;
}

interface DashboardMetrics {
  total_products: number;
  grade_distribution: {
    'A+': number;
    'A': number;
    'B': number;
    'C': number;
    'D': number;
    'Unrated': number;
  };
  critical_count: number;
  repeat_purchase_percentage: number;
}

interface BatchStats {
  batch_id: string;
  batch_number: string;
  product_id: string;
  product_name: string;
  sku: string;
  brand: string | null;
  form: string;
  received_at: string;
  expires_at: string | null;
  days_since_received: number;
  days_until_expiry: number | null;
  status: string;
  review_count: number;
  avg_stars: number;
  product_avg_stars: number; // Product's overall average for comparison
  variance_from_product: number; // Difference from product average
  repeat_purchase_count: number;
  no_repeat_purchase_count: number;
  // Detailed ratings
  avg_scent_rating: number | null;
  avg_dryness_rating: number | null;
  avg_taste_rating: number | null;
  avg_appearance_rating: number | null;
  avg_packaging_rating: number | null;
  // Product averages for comparison
  product_avg_scent: number | null;
  product_avg_dryness: number | null;
  product_avg_taste: number | null;
  product_avg_appearance: number | null;
  product_avg_packaging: number | null;
  best_used_day: number;
  best_used_night: number;
  best_used_both: number;
  first_review_date: string | null;
  last_review_date: string | null;
}

interface BatchMetrics {
  total_batches: number;
  active_batches: number;
  underperforming_batches: number;
  avg_batch_rating: number;
  avg_consistency_score: number;
}

type SortOption = 'top-rated' | 'poorly-rated' | 'most-reviewed';
type BatchSortOption = 'newest' | 'oldest' | 'top-rated' | 'poorly-rated' | 'most-variance';

export function ClinicDashboard() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'products' | 'batches'>('products');
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [batchMetrics, setBatchMetrics] = useState<BatchMetrics | null>(null);
  const [products, setProducts] = useState<ProductStats[]>([]);
  const [batches, setBatches] = useState<BatchStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('most-reviewed');
  const [batchSortBy, setBatchSortBy] = useState<BatchSortOption>('newest');

  // Check if user is clinic
  if (!profile || profile.role !== 'clinic') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              This page is only accessible to clinic users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.href = '/'}>
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ✅ FIX 1-3: Define async functions BEFORE useEffect with proper dependencies
  const loadDashboardData = useCallback(async () => {
    if (!profile.clinic_id) {
      setLoading(false);
      return;
    }

    let isMounted = true; // ✅ FIX 15: Prevent memory leaks

    try {
      setLoading(true);

      // 🔧 DEVELOPMENT MODE - Use mock data
      if (config.devMode.enabled && config.devMode.mockData) {
        if (config.devMode.logging) {
          console.log('🔧 DEV MODE: Loading mock clinic dashboard data'); // ✅ FIX 9: Conditional logging
        }
        
        const mockProducts: ProductStats[] = [
          {
            product_id: 'product-1',
            product_name: 'Blue Dream',
            sku: 'MWL-001',
            brand: 'Premium Cannabis Co',
            form: 'flower',
            review_count: 24,
            avg_stars: 4.8,
            repeat_purchase_count: 20,
            no_repeat_purchase_count: 4,
            recent_low_ratings: 0,
            last_review_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            avg_scent_rating: 4.7,
            avg_dryness_rating: 4.6,
            avg_taste_rating: 4.9,
            avg_appearance_rating: 4.8,
            avg_packaging_rating: 4.5,
            best_used_day: 8,
            best_used_night: 12,
            best_used_both: 4,
          },
          {
            product_id: 'product-2',
            product_name: 'Northern Lights',
            sku: 'MWL-002',
            brand: 'Green Leaf Co',
            form: 'flower',
            review_count: 18,
            avg_stars: 4.2,
            repeat_purchase_count: 14,
            no_repeat_purchase_count: 4,
            recent_low_ratings: 0,
            last_review_date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            avg_scent_rating: 4.3,
            avg_dryness_rating: 4.0,
            avg_taste_rating: 4.4,
            avg_appearance_rating: 4.2,
            avg_packaging_rating: 4.1,
            best_used_day: 3,
            best_used_night: 14,
            best_used_both: 1,
          },
          {
            product_id: 'product-3',
            product_name: 'CBD Oil 20mg/ml',
            sku: 'MWL-003',
            brand: 'Medical Cannabis Ltd',
            form: 'oil',
            review_count: 32,
            avg_stars: 4.9,
            repeat_purchase_count: 30,
            no_repeat_purchase_count: 2,
            recent_low_ratings: 0,
            last_review_date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            avg_scent_rating: null,
            avg_dryness_rating: null,
            avg_taste_rating: 4.8,
            avg_appearance_rating: 4.9,
            avg_packaging_rating: 4.7,
            best_used_day: 16,
            best_used_night: 8,
            best_used_both: 8,
          },
          {
            product_id: 'product-4',
            product_name: 'Sour Diesel',
            sku: 'MWL-004',
            brand: 'Premium Cannabis Co',
            form: 'flower',
            review_count: 15,
            avg_stars: 3.8,
            repeat_purchase_count: 8,
            no_repeat_purchase_count: 7,
            recent_low_ratings: 2,
            last_review_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
            avg_scent_rating: 3.5,
            avg_dryness_rating: 3.2,
            avg_taste_rating: 3.9,
            avg_appearance_rating: 4.1,
            avg_packaging_rating: 3.6,
            best_used_day: 10,
            best_used_night: 3,
            best_used_both: 2,
          },
          {
            product_id: 'product-5',
            product_name: 'Purple Kush',
            sku: 'MWL-005',
            brand: 'Green Leaf Co',
            form: 'flower',
            review_count: 27,
            avg_stars: 4.6,
            repeat_purchase_count: 22,
            no_repeat_purchase_count: 5,
            recent_low_ratings: 0,
            last_review_date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
            avg_scent_rating: 4.7,
            avg_dryness_rating: 4.4,
            avg_taste_rating: 4.8,
            avg_appearance_rating: 4.6,
            avg_packaging_rating: 4.3,
            best_used_day: 5,
            best_used_night: 20,
            best_used_both: 2,
          },
          {
            product_id: 'product-6',
            product_name: 'CBD Capsules 25mg',
            sku: 'MWL-006',
            brand: 'Medical Cannabis Ltd',
            form: 'capsule',
            review_count: 12,
            avg_stars: 4.1,
            repeat_purchase_count: 9,
            no_repeat_purchase_count: 3,
            recent_low_ratings: 0,
            last_review_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            avg_scent_rating: null,
            avg_dryness_rating: null,
            avg_taste_rating: null,
            avg_appearance_rating: 4.2,
            avg_packaging_rating: 4.0,
            best_used_day: 7,
            best_used_night: 3,
            best_used_both: 2,
          },
          {
            product_id: 'product-7',
            product_name: 'OG Kush',
            sku: 'MWL-007',
            brand: 'Premium Cannabis Co',
            form: 'flower',
            review_count: 8,
            avg_stars: 2.8,
            repeat_purchase_count: 2,
            no_repeat_purchase_count: 6,
            recent_low_ratings: 4,
            last_review_date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            avg_scent_rating: 2.5,
            avg_dryness_rating: 2.0,
            avg_taste_rating: 3.1,
            avg_appearance_rating: 3.2,
            avg_packaging_rating: 2.8,
            best_used_day: 4,
            best_used_night: 3,
            best_used_both: 1,
          },
        ];

        if (!isMounted) return; // ✅ FIX 15: Check mount status
        setProducts(mockProducts);
        setMetrics(calculateMetrics(mockProducts));
        setLoading(false);
        return;
      }

      // Fetch clinic's products with review stats
      const { data: orgProducts, error: orgError } = await supabase
        .from('org_products')
        .select(`
          product_id,
          products (
            id,
            product_name,
            sku,
            brand,
            form
          )
        `)
        .eq('org_id', profile.clinic_id);

      if (orgError) {
        console.error('Error fetching org products:', orgError);
        setLoading(false);
        return;
      }

      if (!orgProducts || orgProducts.length === 0) {
        setMetrics({
          total_products: 0,
          grade_distribution: {
            'A+': 0,
            'A': 0,
            'B': 0,
            'C': 0,
            'D': 0,
            'Unrated': 0,
          },
          critical_count: 0,
          repeat_purchase_percentage: 0,
        });
        setProducts([]);
        setLoading(false);
        return;
      }

      // Get review stats for each product
      const productStats = await Promise.all(
        orgProducts.map(async (op: any) => {
          const product = op.products;
          if (!product) return null;

          // Get all approved reviews for this product with detailed ratings
          const { data: reviews } = await supabase
            .from('reviews')
            .select('stars, repeat_purchase, submitted_at, scent_rating, dryness_rating, taste_rating, appearance_rating, packaging_rating, best_used')
            .eq('product_id', product.id)
            .eq('mod_state', 'approved');

          const reviewCount = reviews?.length || 0;
          const avgStars = reviewCount > 0
            ? reviews!.reduce((sum, r) => sum + r.stars, 0) / reviewCount
            : 0;

          const repeatPurchaseCount = reviews?.filter(r => r.repeat_purchase === true).length || 0;
          const noRepeatPurchaseCount = reviews?.filter(r => r.repeat_purchase === false).length || 0;

          // Count recent low ratings (last 30 days)
          const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
          const recentLowRatings = reviews?.filter(
            r => r.stars <= 2 && r.submitted_at > thirtyDaysAgo
          ).length || 0;

          const lastReviewDate = reviewCount > 0
            ? reviews!.sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())[0].submitted_at
            : null;

          // Calculate detailed rating averages
          const calculateAvg = (field: 'scent_rating' | 'dryness_rating' | 'taste_rating' | 'appearance_rating' | 'packaging_rating') => {
            const validRatings = reviews?.filter(r => r[field] !== null).map(r => r[field]!) || [];
            return validRatings.length > 0 ? validRatings.reduce((sum, r) => sum + r, 0) / validRatings.length : null;
          };

          const bestUsedDay = reviews?.filter(r => r.best_used === 'day').length || 0;
          const bestUsedNight = reviews?.filter(r => r.best_used === 'night').length || 0;
          const bestUsedBoth = reviews?.filter(r => r.best_used === 'both').length || 0;

          return {
            product_id: product.id,
            product_name: product.product_name,
            sku: product.sku,
            brand: product.brand,
            form: product.form,
            review_count: reviewCount,
            avg_stars: avgStars,
            repeat_purchase_count: repeatPurchaseCount,
            no_repeat_purchase_count: noRepeatPurchaseCount,
            recent_low_ratings: recentLowRatings,
            last_review_date: lastReviewDate,
            avg_scent_rating: calculateAvg('scent_rating'),
            avg_dryness_rating: calculateAvg('dryness_rating'),
            avg_taste_rating: calculateAvg('taste_rating'),
            avg_appearance_rating: calculateAvg('appearance_rating'),
            avg_packaging_rating: calculateAvg('packaging_rating'),
            best_used_day: bestUsedDay,
            best_used_night: bestUsedNight,
            best_used_both: bestUsedBoth,
          } as ProductStats;
        })
      );

      const validProducts = productStats.filter((p): p is ProductStats => p !== null);
      if (!isMounted) return; // ✅ FIX 15: Check mount status
      setProducts(validProducts);
      setMetrics(calculateMetrics(validProducts));
    } catch (error) {
      // ✅ FIX 4, 11: Proper error typing and messaging
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      if (config.devMode.logging) {
        console.error('Error loading dashboard data:', errorMessage);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false; // ✅ FIX 15: Cleanup
    };
  }, [profile.clinic_id]); // ✅ FIX 3: Include all dependencies

  const loadBatchData = useCallback(async () => {
    if (!profile.clinic_id) {
      setBatchesLoading(false);
      return;
    }

    let isMounted = true; // ✅ FIX 15: Prevent memory leaks

    try {
      setBatchesLoading(true);

      // Fetch clinic's products first to get product IDs
      const { data: orgProducts, error: orgError } = await supabase
        .from('org_products')
        .select('product_id')
        .eq('org_id', profile.clinic_id);

      if (orgError || !orgProducts || orgProducts.length === 0) {
        console.error('Error fetching org products:', orgError);
        setBatches([]);
        setBatchMetrics({
          total_batches: 0,
          active_batches: 0,
          underperforming_batches: 0,
          avg_batch_rating: 0,
          avg_consistency_score: 0,
        });
        setBatchesLoading(false);
        return;
      }

      const productIds = orgProducts.map(op => op.product_id);

      // Fetch all batches for these products with product details
      const { data: batchesData, error: batchesError } = await supabase
        .from('batches')
        .select(`
          id,
          batch_number,
          product_id,
          received_at,
          expires_at,
          status,
          products (
            id,
            product_name,
            sku,
            brand,
            form
          )
        `)
        .in('product_id', productIds)
        .order('received_at', { ascending: false });

      if (batchesError) {
        console.error('Error fetching batches:', batchesError);
        setBatchesLoading(false);
        return;
      }

      if (!batchesData || batchesData.length === 0) {
        setBatches([]);
        setBatchMetrics({
          total_batches: 0,
          active_batches: 0,
          underperforming_batches: 0,
          avg_batch_rating: 0,
          avg_consistency_score: 0,
        });
        setBatchesLoading(false);
        return;
      }

      // For each batch, calculate stats
      const batchStats = await Promise.all(
        batchesData.map(async (batch: any) => {
          const product = batch.products;
          if (!product) return null;

          // Get reviews for this specific batch
          const { data: batchReviews } = await supabase
            .from('reviews')
            .select('stars, repeat_purchase, submitted_at, scent_rating, dryness_rating, taste_rating, appearance_rating, packaging_rating, best_used')
            .eq('batch_id', batch.id)
            .eq('mod_state', 'approved');

          // Get all reviews for the product (for comparison)
          const { data: productReviews } = await supabase
            .from('reviews')
            .select('stars, scent_rating, dryness_rating, taste_rating, appearance_rating, packaging_rating')
            .eq('product_id', product.id)
            .eq('mod_state', 'approved');

          const batchReviewCount = batchReviews?.length || 0;
          const batchAvgStars = batchReviewCount > 0
            ? batchReviews!.reduce((sum, r) => sum + r.stars, 0) / batchReviewCount
            : 0;

          const productReviewCount = productReviews?.length || 0;
          const productAvgStars = productReviewCount > 0
            ? productReviews!.reduce((sum, r) => sum + r.stars, 0) / productReviewCount
            : 0;

          const varianceFromProduct = batchAvgStars - productAvgStars;

          const repeatPurchaseCount = batchReviews?.filter(r => r.repeat_purchase === true).length || 0;
          const noRepeatPurchaseCount = batchReviews?.filter(r => r.repeat_purchase === false).length || 0;

          // Calculate detailed rating averages for batch
          const calculateBatchAvg = (field: 'scent_rating' | 'dryness_rating' | 'taste_rating' | 'appearance_rating' | 'packaging_rating') => {
            const validRatings = batchReviews?.filter(r => r[field] !== null).map(r => r[field]!) || [];
            return validRatings.length > 0 ? validRatings.reduce((sum, r) => sum + r, 0) / validRatings.length : null;
          };

          // Calculate detailed rating averages for product
          const calculateProductAvg = (field: 'scent_rating' | 'dryness_rating' | 'taste_rating' | 'appearance_rating' | 'packaging_rating') => {
            const validRatings = productReviews?.filter(r => r[field] !== null).map(r => r[field]!) || [];
            return validRatings.length > 0 ? validRatings.reduce((sum, r) => sum + r, 0) / validRatings.length : null;
          };

          const bestUsedDay = batchReviews?.filter(r => r.best_used === 'day').length || 0;
          const bestUsedNight = batchReviews?.filter(r => r.best_used === 'night').length || 0;
          const bestUsedBoth = batchReviews?.filter(r => r.best_used === 'both').length || 0;

          const firstReviewDate = batchReviewCount > 0
            ? batchReviews!.sort((a, b) => new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime())[0].submitted_at
            : null;

          const lastReviewDate = batchReviewCount > 0
            ? batchReviews!.sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())[0].submitted_at
            : null;

          const receivedDate = new Date(batch.received_at);
          const now = new Date();
          const daysSinceReceived = Math.floor((now.getTime() - receivedDate.getTime()) / (1000 * 60 * 60 * 24));
          
          let daysUntilExpiry = null;
          if (batch.expires_at) {
            const expiryDate = new Date(batch.expires_at);
            daysUntilExpiry = Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          }

          return {
            batch_id: batch.id,
            batch_number: batch.batch_number,
            product_id: product.id,
            product_name: product.product_name,
            sku: product.sku,
            brand: product.brand,
            form: product.form,
            received_at: batch.received_at,
            expires_at: batch.expires_at,
            days_since_received: daysSinceReceived,
            days_until_expiry: daysUntilExpiry,
            status: batch.status,
            review_count: batchReviewCount,
            avg_stars: batchAvgStars,
            product_avg_stars: productAvgStars,
            variance_from_product: varianceFromProduct,
            repeat_purchase_count: repeatPurchaseCount,
            no_repeat_purchase_count: noRepeatPurchaseCount,
            avg_scent_rating: calculateBatchAvg('scent_rating'),
            avg_dryness_rating: calculateBatchAvg('dryness_rating'),
            avg_taste_rating: calculateBatchAvg('taste_rating'),
            avg_appearance_rating: calculateBatchAvg('appearance_rating'),
            avg_packaging_rating: calculateBatchAvg('packaging_rating'),
            product_avg_scent: calculateProductAvg('scent_rating'),
            product_avg_dryness: calculateProductAvg('dryness_rating'),
            product_avg_taste: calculateProductAvg('taste_rating'),
            product_avg_appearance: calculateProductAvg('appearance_rating'),
            product_avg_packaging: calculateProductAvg('packaging_rating'),
            best_used_day: bestUsedDay,
            best_used_night: bestUsedNight,
            best_used_both: bestUsedBoth,
            first_review_date: firstReviewDate,
            last_review_date: lastReviewDate,
          } as BatchStats;
        })
      );

      const validBatches = batchStats.filter((b): b is BatchStats => b !== null);
      if (!isMounted) return; // ✅ FIX 15: Check mount status
      setBatches(validBatches);
      setBatchMetrics(calculateBatchMetrics(validBatches));
    } catch (error) {
      // ✅ FIX 4, 11: Proper error typing and messaging
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      if (config.devMode.logging) {
        console.error('Error loading batch data:', errorMessage);
      }
    } finally {
      if (isMounted) {
        setBatchesLoading(false);
      }
    }

    return () => {
      isMounted = false; // ✅ FIX 15: Cleanup
    };
  }, [profile.clinic_id]); // ✅ FIX 14: Include dependencies

  // ✅ FIX 1: useEffect AFTER function declarations with proper deps
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]); // ✅ Use memoized function

  // ✅ FIX 2: Load batch data when switching to batches tab with proper deps
  useEffect(() => {
    if (activeTab === 'batches' && batches.length === 0 && !batchesLoading) {
      loadBatchData();
    }
  }, [activeTab, batches.length, batchesLoading, loadBatchData]); // ✅ Exhaustive deps

  const calculateMetrics = (products: ProductStats[]): DashboardMetrics => {
    const gradeDistribution = {
      'A+': 0,
      'A': 0,
      'B': 0,
      'C': 0,
      'D': 0,
      'Unrated': 0,
    };

    let criticalCount = 0;
    let totalRepeatPurchaseYes = 0;
    let totalRepeatPurchaseNo = 0;

    products.forEach(product => {
      const grade = getGrade(product.avg_stars, product.review_count);
      gradeDistribution[grade as keyof typeof gradeDistribution]++;

      if (product.avg_stars < 3.0 && product.review_count >= 3) {
        criticalCount++;
      }
      if (product.recent_low_ratings >= 2) {
        criticalCount++;
      }

      totalRepeatPurchaseYes += product.repeat_purchase_count;
      totalRepeatPurchaseNo += product.no_repeat_purchase_count;
    });

    const totalRepeatPurchaseResponses = totalRepeatPurchaseYes + totalRepeatPurchaseNo;
    const repeatPurchasePercentage = totalRepeatPurchaseResponses > 0
      ? Math.round((totalRepeatPurchaseYes / totalRepeatPurchaseResponses) * 100)
      : 0;

    return {
      total_products: products.length,
      grade_distribution: gradeDistribution,
      critical_count: criticalCount,
      repeat_purchase_percentage: repeatPurchasePercentage,
    };
  };

  const getGrade = (avgStars: number, reviewCount: number): string => {
    if (reviewCount === 0) return 'Unrated';
    if (avgStars >= 4.8) return 'A+';
    if (avgStars >= 4.5) return 'A';
    if (avgStars >= 4.0) return 'B';
    if (avgStars >= 3.0) return 'C';
    return 'D';
  };

  const getGradeColor = (grade: string): string => {
    switch (grade) {
      case 'A+':
      case 'A':
        return 'text-primary bg-primary/5 border-primary/20';
      case 'B':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'C':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'D':
        return 'text-destructive bg-destructive/5 border-destructive/20';
      default:
        return 'text-muted-foreground bg-muted/50 border-muted';
    }
  };

  const calculateBatchMetrics = (batches: BatchStats[]): BatchMetrics => {
    const activeBatches = batches.filter(b => b.status === 'in_stock').length;
    
    // Batches rated 0.5 stars or more below product average
    const underperformingBatches = batches.filter(
      b => b.review_count >= 3 && b.variance_from_product <= -0.5
    ).length;

    const reviewedBatches = batches.filter(b => b.review_count > 0);
    const avgBatchRating = reviewedBatches.length > 0
      ? reviewedBatches.reduce((sum, b) => sum + b.avg_stars, 0) / reviewedBatches.length
      : 0;

    // Consistency score: lower variance = more consistent
    const varianceScores = reviewedBatches.map(b => Math.abs(b.variance_from_product));
    const avgConsistencyScore = varianceScores.length > 0
      ? 5 - (varianceScores.reduce((sum, v) => sum + v, 0) / varianceScores.length) // Inverse of variance
      : 5;

    return {
      total_batches: batches.length,
      active_batches: activeBatches,
      underperforming_batches: underperformingBatches,
      avg_batch_rating: avgBatchRating,
      avg_consistency_score: Math.max(0, Math.min(5, avgConsistencyScore)),
    };
  };

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case 'top-rated':
        if (a.review_count === 0) return 1;
        if (b.review_count === 0) return -1;
        return b.avg_stars - a.avg_stars;
      case 'poorly-rated':
        if (a.review_count === 0) return 1;
        if (b.review_count === 0) return -1;
        return a.avg_stars - b.avg_stars;
      case 'most-reviewed':
      default:
        return b.review_count - a.review_count;
    }
  });

  const sortedBatches = [...batches].sort((a, b) => {
    switch (batchSortBy) {
      case 'newest':
        return new Date(b.received_at).getTime() - new Date(a.received_at).getTime();
      case 'oldest':
        return new Date(a.received_at).getTime() - new Date(b.received_at).getTime();
      case 'top-rated':
        if (a.review_count === 0) return 1;
        if (b.review_count === 0) return -1;
        return b.avg_stars - a.avg_stars;
      case 'poorly-rated':
        if (a.review_count === 0) return 1;
        if (b.review_count === 0) return -1;
        return a.avg_stars - b.avg_stars;
      case 'most-variance':
        return Math.abs(b.variance_from_product) - Math.abs(a.variance_from_product);
      default:
        return new Date(b.received_at).getTime() - new Date(a.received_at).getTime();
    }
  });

  return (
    <div className="min-h-screen bg-background pt-24 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl gradient-green flex items-center justify-center shadow-lg">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1>Clinic Dashboard</h1>
                <p className="text-muted-foreground mt-1">{profile.display_name}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => window.location.hash = '#/clinic-verification'}
                className="transition-smooth hover-lift elevation-sm"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Patient Verification
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.hash = '#/org-settings'}
                className="transition-smooth hover-lift elevation-sm"
              >
                <Settings className="w-4 h-4 mr-2" />
                Profile & Settings
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[1, 2, 3, 4].map(i => (
                <Card key={i} className="border-border/50 shadow-sm">
                  <CardHeader className="pb-3">
                    <Skeleton className="h-4 w-24 skeleton-shimmer" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-9 w-16 skeleton-shimmer" />
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card className="border-border/50 shadow-sm">
              <CardHeader className="pb-6">
                <Skeleton className="h-6 w-48 skeleton-shimmer" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-64 w-full skeleton-shimmer" />
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            {/* Metrics Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              {/* Total Products */}
              <Card className="border-border/50 shadow-sm hover-lift transition-smooth">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Package className="w-4 h-4 text-primary" />
                    </div>
                    <span>Total Products</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="stat-number">{metrics?.total_products || 0}</div>
                </CardContent>
              </Card>

              {/* Grade Distribution */}
              <Card className="border-border/50 shadow-sm hover-lift transition-smooth">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Star className="w-4 h-4 text-amber-500" />
                    </div>
                    <span>Grade Distribution</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {metrics && Object.entries(metrics.grade_distribution).map(([grade, count]) => (
                      count > 0 && grade !== 'Unrated' && (
                        <Badge 
                          key={grade} 
                          variant="outline" 
                          className={`${getGradeColor(grade)} border-current/20`}
                        >
                          {grade}: {count}
                        </Badge>
                      )
                    ))}
                    {metrics && metrics.grade_distribution.Unrated > 0 && (
                      <Badge variant="outline" className="text-muted-foreground border-muted">
                        Unrated: {metrics.grade_distribution.Unrated}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Critical Ratings */}
              <Card className="border-border/50 shadow-sm hover-lift transition-smooth">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-destructive" />
                    </div>
                    <span>Critical Alerts</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="stat-number text-destructive">
                    {metrics?.critical_count || 0}
                  </div>
                  {metrics && metrics.critical_count > 0 && (
                    <p className="text-muted-foreground mt-1.5">Need attention</p>
                  )}
                </CardContent>
              </Card>

              {/* Repeat Purchase Rate */}
              <Card className="border-border/50 shadow-sm hover-lift transition-smooth">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <ThumbsUp className="w-4 h-4 text-primary" />
                    </div>
                    <span>Would Buy Again</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <div className="stat-number text-primary">
                      {metrics?.repeat_purchase_percentage || 0}%
                    </div>
                    {metrics && metrics.repeat_purchase_percentage < 50 && (
                      <ThumbsDown className="w-5 h-5 text-destructive" />
                    )}
                    {metrics && metrics.repeat_purchase_percentage >= 70 && (
                      <TrendingUp className="w-5 h-5 text-primary" />
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Products Table */}
            <Card className="border-border/50 shadow-sm">
              <CardHeader className="pb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="mb-2">Product Performance</CardTitle>
                    <CardDescription>
                      Detailed review insights including scent, taste, appearance, packaging ratings, and patient preferences
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
                      <SelectTrigger className="w-full sm:w-[220px] transition-smooth hover:border-primary/50">
                        <ArrowUpDown className="w-4 h-4 mr-2 text-muted-foreground" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="most-reviewed">Most Reviewed</SelectItem>
                        <SelectItem value="top-rated">Top Rated</SelectItem>
                        <SelectItem value="poorly-rated">Poorly Rated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-0 sm:px-6">
                {products.length === 0 ? (
                  <div className="text-center py-16 px-4">
                    <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center">
                      <Package className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="mb-2">No products listed yet</h3>
                    <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                      Add products to your clinic catalogue to start tracking performance and reviews
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => window.location.hash = '#/org-settings'}
                      className="transition-smooth hover-lift"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Add Products
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border/70">
                          <th className="text-left py-4 px-4 sm:px-6 text-muted-foreground">Product</th>
                          <th className="text-left py-4 px-4 sm:px-6 text-muted-foreground">SKU</th>
                          <th className="text-center py-4 px-4 sm:px-6 text-muted-foreground">Grade</th>
                          <th className="text-center py-4 px-4 sm:px-6 text-muted-foreground">Reviews</th>
                          <th className="text-center py-4 px-4 sm:px-6 text-muted-foreground">Avg Rating</th>
                          <th className="text-center py-4 px-4 sm:px-6 text-muted-foreground">Scent</th>
                          <th className="text-center py-4 px-4 sm:px-6 text-muted-foreground">Taste</th>
                          <th className="text-center py-4 px-4 sm:px-6 text-muted-foreground">Appearance</th>
                          <th className="text-center py-4 px-4 sm:px-6 text-muted-foreground">Packaging</th>
                          <th className="text-center py-4 px-4 sm:px-6 text-muted-foreground">Best Used</th>
                          <th className="text-center py-4 px-4 sm:px-6 text-muted-foreground">Buy Again</th>
                          <th className="text-center py-4 px-4 sm:px-6 text-muted-foreground">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {sortedProducts.map((product) => {
                          const grade = getGrade(product.avg_stars, product.review_count);
                          const isCritical = product.avg_stars < 3.0 && product.review_count >= 3;
                          const hasRecentIssues = product.recent_low_ratings >= 2;
                          const buyAgainPercentage = 
                            (product.repeat_purchase_count + product.no_repeat_purchase_count) > 0
                              ? Math.round((product.repeat_purchase_count / (product.repeat_purchase_count + product.no_repeat_purchase_count)) * 100)
                              : 0;

                          return (
                            <tr
                              key={product.product_id}
                              className="group hover:bg-accent/30 transition-smooth cursor-pointer"
                              onClick={() => window.location.hash = `#/clinic-product-detail/${product.product_id}`}
                            >
                              <td className="py-4 px-4 sm:px-6">
                                <div>
                                  <div className="group-hover:text-primary transition-colors">{product.product_name}</div>
                                  {product.brand && (
                                    <div className="text-muted-foreground mt-0.5">{product.brand}</div>
                                  )}
                                </div>
                              </td>
                              <td className="py-4 px-4 sm:px-6">
                                <code className="bg-muted px-2.5 py-1 rounded-md border border-border/50">
                                  {product.sku}
                                </code>
                              </td>
                              <td className="py-4 px-4 sm:px-6 text-center">
                                <Badge 
                                  variant="outline" 
                                  className={`${getGradeColor(grade)} border-current/20`}
                                >
                                  {grade}
                                </Badge>
                              </td>
                              <td className="py-4 px-4 sm:px-6 text-center">
                                <span className="stat-number">{product.review_count}</span>
                              </td>
                              <td className="py-4 px-4 sm:px-6 text-center">
                                {product.review_count > 0 ? (
                                  <div className="flex items-center justify-center gap-1.5">
                                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                    <span className="stat-number">{product.avg_stars.toFixed(1)}</span>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              {/* Scent Rating */}
                              <td className="py-4 px-4 sm:px-6 text-center">
                                {product.avg_scent_rating !== null ? (
                                  <span className={`stat-number ${product.avg_scent_rating >= 4 ? 'text-primary' : product.avg_scent_rating >= 3 ? 'text-foreground' : 'text-destructive'}`}>
                                    {product.avg_scent_rating.toFixed(1)}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              {/* Taste Rating */}
                              <td className="py-4 px-4 sm:px-6 text-center">
                                {product.avg_taste_rating !== null ? (
                                  <span className={`stat-number ${product.avg_taste_rating >= 4 ? 'text-primary' : product.avg_taste_rating >= 3 ? 'text-foreground' : 'text-destructive'}`}>
                                    {product.avg_taste_rating.toFixed(1)}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              {/* Appearance Rating */}
                              <td className="py-4 px-4 sm:px-6 text-center">
                                {product.avg_appearance_rating !== null ? (
                                  <span className={`stat-number ${product.avg_appearance_rating >= 4 ? 'text-primary' : product.avg_appearance_rating >= 3 ? 'text-foreground' : 'text-destructive'}`}>
                                    {product.avg_appearance_rating.toFixed(1)}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              {/* Packaging Rating */}
                              <td className="py-4 px-4 sm:px-6 text-center">
                                {product.avg_packaging_rating !== null ? (
                                  <span className={`stat-number ${product.avg_packaging_rating >= 4 ? 'text-primary' : product.avg_packaging_rating >= 3 ? 'text-foreground' : 'text-destructive'}`}>
                                    {product.avg_packaging_rating.toFixed(1)}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              {/* Best Used */}
                              <td className="py-4 px-4 sm:px-6 text-center">
                                {(product.best_used_day + product.best_used_night + product.best_used_both) > 0 ? (
                                  <div className="flex flex-col gap-1 items-center">
                                    {product.best_used_day > 0 && (
                                      <Badge variant="outline" className="text-amber-600 bg-amber-50 border-amber-200 gap-1">
                                        <Sun className="w-3 h-3" />
                                        Day: {product.best_used_day}
                                      </Badge>
                                    )}
                                    {product.best_used_night > 0 && (
                                      <Badge variant="outline" className="text-indigo-600 bg-indigo-50 border-indigo-200 gap-1">
                                        <Moon className="w-3 h-3" />
                                        Night: {product.best_used_night}
                                      </Badge>
                                    )}
                                    {product.best_used_both > 0 && (
                                      <Badge variant="outline" className="text-primary bg-primary/5 border-primary/20 gap-1">
                                        <Sparkles className="w-3 h-3" />
                                        Both: {product.best_used_both}
                                      </Badge>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              {/* Buy Again */}
                              <td className="py-4 px-4 sm:px-6 text-center">
                                {(product.repeat_purchase_count + product.no_repeat_purchase_count) > 0 ? (
                                  <div className="flex items-center justify-center gap-1.5">
                                    {buyAgainPercentage >= 70 ? (
                                      <TrendingUp className="w-4 h-4 text-primary" />
                                    ) : buyAgainPercentage < 50 ? (
                                      <TrendingDown className="w-4 h-4 text-destructive" />
                                    ) : null}
                                    <span className="stat-number">{buyAgainPercentage}%</span>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="py-4 px-4 sm:px-6 text-center">
                                {isCritical || hasRecentIssues ? (
                                  <Badge variant="destructive" className="gap-1.5 shadow-sm">
                                    <AlertTriangle className="w-3 h-3" />
                                    Critical
                                  </Badge>
                                ) : product.review_count > 0 ? (
                                  <Badge 
                                    variant="outline" 
                                    className="text-primary bg-primary/5 border-primary/20"
                                  >
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="border-muted">New</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Critical Products Alert */}
            {metrics && metrics.critical_count > 0 && (
              <Alert variant="destructive" className="mt-8 border-destructive/30 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                  </div>
                  <AlertDescription className="flex-1">
                    <strong className="block mb-1">Action Required</strong>
                    <p className="text-destructive/90">
                      {metrics.critical_count} product{metrics.critical_count > 1 ? 's have' : ' has'} critical ratings. 
                      Review these products and consider contacting affected patients or improving quality.
                    </p>
                  </AlertDescription>
                </div>
              </Alert>
            )}
          </>
        )}
      </div>
    </div>
  );
}